import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Card,
  CardContent,
  Avatar,
  Stack,
  Tooltip,
  Switch,
  FormControlLabel,
  InputAdornment,
  Divider,
  LinearProgress,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  AttachMoney as MoneyIcon,
  Storage as StorageIcon,
  Login as LoginIcon,
  Settings as SettingsIcon,
  Block as BlockIcon,
  Check as CheckIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

interface Tenant {
  _id: string;
  name: string;
  subdomain: string;
  database: string;
  plan: string;
  status: 'active' | 'trial' | 'suspended' | 'cancelled';
  config: {
    branding: {
      primaryColor?: string;
      secondaryColor?: string;
      logo?: string;
    };
    features: {
      maxProperties?: number;
      maxUsers?: number;
      customBranding?: boolean;
      apiAccess?: boolean;
    };
    limits: {
      monthlyBookings?: number;
      storageGB?: number;
    };
  };
  billing: {
    customerId?: string;
    subscriptionId?: string;
    currentPeriodEnd?: string;
    monthlyRevenue?: number;
  };
  usage: {
    properties: number;
    users: number;
    bookings: number;
    storageUsedGB: number;
  };
  createdAt: string;
  owner: {
    firstName: string;
    lastName: string;
    email: string;
  };
}

interface TenantStats {
  totalTenants: number;
  activeTenants: number;
  trialTenants: number;
  totalMRR: number;
  averageUsage: {
    properties: number;
    users: number;
    bookings: number;
  };
}

const TenantsPage: React.FC = () => {
  const { user, switchTenant } = useAuth();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [stats, setStats] = useState<TenantStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalTenants, setTotalTenants] = useState(0);
  
  // Dialog states
  const [tenantDialog, setTenantDialog] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [loginAsDialog, setLoginAsDialog] = useState(false);
  
  // Form state
  const [tenantForm, setTenantForm] = useState({
    name: '',
    subdomain: '',
    plan: 'professional',
    owner: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
    },
    config: {
      branding: {
        primaryColor: '',
        secondaryColor: '',
        logo: '',
      },
      features: {
        maxProperties: 10,
        maxUsers: 50,
        customBranding: true,
        apiAccess: false,
      },
      limits: {
        monthlyBookings: 1000,
        storageGB: 10,
      },
    },
  });

  useEffect(() => {
    if (user?.isPlatformAdmin) {
      fetchTenants();
      fetchStats();
    }
  }, [page, rowsPerPage, user]);

  const fetchTenants = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        limit: rowsPerPage.toString(),
      });
      
      const response = await api.get(`/platform/tenants?${params}`);
      setTenants(response.data.tenants);
      setTotalTenants(response.data.total);
    } catch (error) {
      console.error('Error fetching tenants:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/platform/tenants/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleCreateTenant = () => {
    setSelectedTenant(null);
    setTenantForm({
      name: '',
      subdomain: '',
      plan: 'professional',
      owner: {
        firstName: '',
        lastName: '',
        email: '',
        password: '',
      },
      config: {
        branding: {
          primaryColor: '',
          secondaryColor: '',
          logo: '',
        },
        features: {
          maxProperties: 10,
          maxUsers: 50,
          customBranding: true,
          apiAccess: false,
        },
        limits: {
          monthlyBookings: 1000,
          storageGB: 10,
        },
      },
    });
    setTenantDialog(true);
  };

  const handleEditTenant = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setTenantForm({
      name: tenant.name,
      subdomain: tenant.subdomain,
      plan: tenant.plan,
      owner: {
        firstName: tenant.owner.firstName,
        lastName: tenant.owner.lastName,
        email: tenant.owner.email,
        password: '', // Don't pre-fill password
      },
      config: {
        branding: {
          primaryColor: tenant.config.branding?.primaryColor || '',
          secondaryColor: tenant.config.branding?.secondaryColor || '',
          logo: tenant.config.branding?.logo || '',
        },
        features: {
          maxProperties: tenant.config.features?.maxProperties || 10,
          maxUsers: tenant.config.features?.maxUsers || 50,
          customBranding: tenant.config.features?.customBranding ?? true,
          apiAccess: tenant.config.features?.apiAccess ?? false,
        },
        limits: {
          monthlyBookings: tenant.config.limits?.monthlyBookings || 1000,
          storageGB: tenant.config.limits?.storageGB || 10,
        },
      },
    });
    setTenantDialog(true);
  };

  const handleSaveTenant = async () => {
    try {
      if (selectedTenant) {
        await api.put(`/platform/tenants/${selectedTenant._id}`, tenantForm);
      } else {
        await api.post('/platform/tenants', tenantForm);
      }
      
      setTenantDialog(false);
      fetchTenants();
      fetchStats();
    } catch (error) {
      console.error('Error saving tenant:', error);
    }
  };

  const handleDeleteTenant = async () => {
    if (!selectedTenant) return;
    
    try {
      await api.delete(`/platform/tenants/${selectedTenant._id}`);
      setDeleteDialog(false);
      fetchTenants();
      fetchStats();
    } catch (error) {
      console.error('Error deleting tenant:', error);
    }
  };

  const handleLoginAsTenant = async () => {
    if (!selectedTenant || !switchTenant) return;
    
    try {
      await switchTenant(selectedTenant._id);
      setLoginAsDialog(false);
      // Redirect to tenant dashboard
      window.location.href = '/';
    } catch (error) {
      console.error('Error switching tenant:', error);
    }
  };

  const handleToggleTenantStatus = async (tenant: Tenant) => {
    try {
      const newStatus = tenant.status === 'active' ? 'suspended' : 'active';
      await api.patch(`/platform/tenants/${tenant._id}/status`, { status: newStatus });
      fetchTenants();
    } catch (error) {
      console.error('Error updating tenant status:', error);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: any = {
      active: 'success',
      trial: 'info',
      suspended: 'error',
      cancelled: 'default',
    };
    return colors[status] || 'default';
  };

  const formatPlan = (plan: string) => {
    return plan.charAt(0).toUpperCase() + plan.slice(1);
  };

  const formatUsagePercentage = (used: number, limit: number) => {
    if (!limit) return 0;
    return Math.min((used / limit) * 100, 100);
  };

  if (!user?.isPlatformAdmin) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 4 }}>
          Access denied. Platform admin privileges required.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4" gutterBottom>
            Tenant Management
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateTenant}
          >
            New Tenant
          </Button>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Manage multi-tenant accounts and configurations
        </Typography>
      </Box>

      {/* Stats Overview */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <BusinessIcon color="primary" />
                  <Typography color="text.secondary" variant="subtitle2" sx={{ ml: 1 }}>
                    Total Tenants
                  </Typography>
                </Box>
                <Typography variant="h4">{stats.totalTenants}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {stats.activeTenants} active, {stats.trialTenants} trial
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <MoneyIcon color="success" />
                  <Typography color="text.secondary" variant="subtitle2" sx={{ ml: 1 }}>
                    Monthly Revenue
                  </Typography>
                </Box>
                <Typography variant="h4">${stats.totalMRR.toFixed(2)}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Total MRR
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <TrendingUpIcon color="info" />
                  <Typography color="text.secondary" variant="subtitle2" sx={{ ml: 1 }}>
                    Avg Properties
                  </Typography>
                </Box>
                <Typography variant="h4">{stats.averageUsage.properties.toFixed(1)}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Per tenant
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PersonIcon color="warning" />
                  <Typography color="text.secondary" variant="subtitle2" sx={{ ml: 1 }}>
                    Avg Users
                  </Typography>
                </Box>
                <Typography variant="h4">{stats.averageUsage.users.toFixed(1)}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Per tenant
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tenants Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Tenant</TableCell>
                <TableCell>Owner</TableCell>
                <TableCell>Plan</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Usage</TableCell>
                <TableCell>Revenue</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : tenants.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No tenants found
                  </TableCell>
                </TableRow>
              ) : (
                tenants.map((tenant) => (
                  <TableRow key={tenant._id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ mr: 2, bgcolor: tenant.config.branding.primaryColor || 'primary.main' }}>
                          {tenant.name.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2">{tenant.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {tenant.subdomain}.equityengine.com
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {tenant.owner.firstName} {tenant.owner.lastName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {tenant.owner.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={formatPlan(tenant.plan)} size="small" />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={tenant.status}
                        color={getStatusColor(tenant.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ minWidth: 120 }}>
                        <Typography variant="caption" display="block">
                          Properties: {tenant.usage.properties}/{tenant.config.features.maxProperties || '∞'}
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={formatUsagePercentage(tenant.usage.properties, tenant.config.features.maxProperties || 0)}
                          sx={{ height: 4, mb: 0.5 }}
                        />
                        <Typography variant="caption" display="block">
                          Users: {tenant.usage.users}/{tenant.config.features.maxUsers || '∞'}
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={formatUsagePercentage(tenant.usage.users, tenant.config.features.maxUsers || 0)}
                          sx={{ height: 4 }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        ${tenant.billing.monthlyRevenue?.toFixed(2) || '0.00'}/mo
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {new Date(tenant.createdAt).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleEditTenant(tenant)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Login as Tenant">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedTenant(tenant);
                              setLoginAsDialog(true);
                            }}
                            disabled={tenant.status !== 'active'}
                          >
                            <LoginIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={tenant.status === 'active' ? 'Suspend' : 'Activate'}>
                          <IconButton
                            size="small"
                            onClick={() => handleToggleTenantStatus(tenant)}
                          >
                            {tenant.status === 'active' ? <BlockIcon fontSize="small" /> : <CheckIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              setSelectedTenant(tenant);
                              setDeleteDialog(true);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          component="div"
          count={totalTenants}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>

      {/* Create/Edit Tenant Dialog */}
      <Dialog
        open={tenantDialog}
        onClose={() => setTenantDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedTenant ? 'Edit Tenant' : 'Create New Tenant'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Tenant Information
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Tenant Name"
                value={tenantForm.name}
                onChange={(e) => setTenantForm({ ...tenantForm, name: e.target.value })}
                required
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Subdomain"
                value={tenantForm.subdomain}
                onChange={(e) => setTenantForm({ ...tenantForm, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                InputProps={{
                  endAdornment: <InputAdornment position="end">.equityengine.com</InputAdornment>,
                }}
                required
                disabled={!!selectedTenant}
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Plan</InputLabel>
                <Select
                  value={tenantForm.plan}
                  label="Plan"
                  onChange={(e) => setTenantForm({ ...tenantForm, plan: e.target.value })}
                >
                  <MenuItem value="starter">Starter</MenuItem>
                  <MenuItem value="professional">Professional</MenuItem>
                  <MenuItem value="enterprise">Enterprise</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <Divider />
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Owner Information
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={tenantForm.owner.firstName}
                onChange={(e) => setTenantForm({
                  ...tenantForm,
                  owner: { ...tenantForm.owner, firstName: e.target.value }
                })}
                required
                disabled={!!selectedTenant}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={tenantForm.owner.lastName}
                onChange={(e) => setTenantForm({
                  ...tenantForm,
                  owner: { ...tenantForm.owner, lastName: e.target.value }
                })}
                required
                disabled={!!selectedTenant}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={tenantForm.owner.email}
                onChange={(e) => setTenantForm({
                  ...tenantForm,
                  owner: { ...tenantForm.owner, email: e.target.value }
                })}
                required
                disabled={!!selectedTenant}
              />
            </Grid>
            
            {!selectedTenant && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Initial Password"
                  type="password"
                  value={tenantForm.owner.password}
                  onChange={(e) => setTenantForm({
                    ...tenantForm,
                    owner: { ...tenantForm.owner, password: e.target.value }
                  })}
                  required
                />
              </Grid>
            )}
            
            <Grid item xs={12}>
              <Divider />
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Features & Limits
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Max Properties"
                type="number"
                value={tenantForm.config.features.maxProperties}
                onChange={(e) => setTenantForm({
                  ...tenantForm,
                  config: {
                    ...tenantForm.config,
                    features: {
                      ...tenantForm.config.features,
                      maxProperties: parseInt(e.target.value) || 0
                    }
                  }
                })}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Max Users"
                type="number"
                value={tenantForm.config.features.maxUsers}
                onChange={(e) => setTenantForm({
                  ...tenantForm,
                  config: {
                    ...tenantForm.config,
                    features: {
                      ...tenantForm.config.features,
                      maxUsers: parseInt(e.target.value) || 0
                    }
                  }
                })}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Monthly Booking Limit"
                type="number"
                value={tenantForm.config.limits.monthlyBookings}
                onChange={(e) => setTenantForm({
                  ...tenantForm,
                  config: {
                    ...tenantForm.config,
                    limits: {
                      ...tenantForm.config.limits,
                      monthlyBookings: parseInt(e.target.value) || 0
                    }
                  }
                })}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Storage Limit (GB)"
                type="number"
                value={tenantForm.config.limits.storageGB}
                onChange={(e) => setTenantForm({
                  ...tenantForm,
                  config: {
                    ...tenantForm.config,
                    limits: {
                      ...tenantForm.config.limits,
                      storageGB: parseInt(e.target.value) || 0
                    }
                  }
                })}
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={tenantForm.config.features.customBranding}
                    onChange={(e) => setTenantForm({
                      ...tenantForm,
                      config: {
                        ...tenantForm.config,
                        features: {
                          ...tenantForm.config.features,
                          customBranding: e.target.checked
                        }
                      }
                    })}
                  />
                }
                label="Enable Custom Branding"
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={tenantForm.config.features.apiAccess}
                    onChange={(e) => setTenantForm({
                      ...tenantForm,
                      config: {
                        ...tenantForm.config,
                        features: {
                          ...tenantForm.config.features,
                          apiAccess: e.target.checked
                        }
                      }
                    })}
                  />
                }
                label="Enable API Access"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTenantDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveTenant} variant="contained">
            {selectedTenant ? 'Update' : 'Create'} Tenant
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Delete Tenant</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              This action cannot be undone. All data associated with this tenant will be permanently deleted.
            </Typography>
          </Alert>
          <Typography>
            Are you sure you want to delete tenant "{selectedTenant?.name}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDeleteTenant} variant="contained" color="error">
            Delete Tenant
          </Button>
        </DialogActions>
      </Dialog>

      {/* Login As Tenant Dialog */}
      <Dialog open={loginAsDialog} onClose={() => setLoginAsDialog(false)}>
        <DialogTitle>Login as Tenant</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              You will be logged in as the tenant administrator. Use the platform admin menu to switch back.
            </Typography>
          </Alert>
          <Typography>
            Switch to tenant "{selectedTenant?.name}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLoginAsDialog(false)}>Cancel</Button>
          <Button onClick={handleLoginAsTenant} variant="contained" startIcon={<LoginIcon />}>
            Login as Tenant
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TenantsPage;